import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminRoleProvider } from './contexts/AdminRoleContext';
import { getBrandConfig } from './config/branding';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Admin from './pages/Admin';
import Players from './pages/Players';
import Leagues from './pages/Leagues';
import Games from './pages/Games';
import Study from './pages/Study';
import PlayerProfile from './pages/PlayerProfile';
import LeagueStats from './pages/LeagueStats';
import SeatingPlanView from './pages/SeatingPlanView';
import LiveGameOverlay from './components/LiveGameOverlay';
import QuizPage from './pages/QuizPage';
import QuizCompleted from './components/quiz/QuizCompleted';

// Define the theme with dynamic branding
const createAppTheme = () => {
  const brandConfig = getBrandConfig();
  
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: brandConfig.primary,
      },
      secondary: {
        main: brandConfig.secondary,
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
    },
    typography: {
      fontFamily: '"Montserrat", "Roboto", "Arial", sans-serif',
      h1: {
        fontFamily: '"Montserrat", sans-serif',
        fontWeight: 700,
      },
      h2: {
        fontFamily: '"Montserrat", sans-serif',
        fontWeight: 700,
      },
      h3: {
        fontFamily: '"Montserrat", sans-serif',
        fontWeight: 600,
      },
      h4: {
        fontFamily: '"Montserrat", sans-serif',
        fontWeight: 600,
      },
      h5: {
        fontFamily: '"Montserrat", sans-serif',
        fontWeight: 600,
      },
      h6: {
        fontFamily: '"Montserrat", sans-serif',
        fontWeight: 600,
      },
      button: {
        fontFamily: '"Montserrat", sans-serif',
        fontWeight: 600,
      },
      subtitle1: {
        fontFamily: '"Montserrat", sans-serif',
      },
      subtitle2: {
        fontFamily: '"Montserrat", sans-serif',
      },
      body1: {
        fontFamily: '"Montserrat", sans-serif',
      },
      body2: {
        fontFamily: '"Montserrat", sans-serif',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@keyframes gradient': {
            '0%': {
              backgroundPosition: '0% 50%'
            },
            '50%': {
              backgroundPosition: '100% 50%'
            },
            '100%': {
              backgroundPosition: '0% 50%'
            }
          },
          'html, body': {
            margin: 0,
            padding: 0,
            height: '100%',
            width: '100%',
            overflow: 'auto',
            fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            letterSpacing: '0.015em'
          },
          body: {
            /* Animated gradient background - applied dynamically via useEffect */
            backgroundSize: '400% 400%',
            animation: 'gradient 15s ease infinite',
            minHeight: '100vh',
            /* Create a dark overlay to make it even darker */
            '&::before': {
              content: '""',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              pointerEvents: 'none',
              zIndex: -1,
            }
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(17, 15, 30, 0.6)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 0,
              background: 'linear-gradient(125deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 40%, rgba(255, 255, 255, 0) 80%, rgba(255, 255, 255, 0.03) 100%)',
              transition: 'opacity 0.3s ease-in-out',
              opacity: 0,
            },
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
              '&::after': {
                opacity: 1,
              }
            },
            '& > *': {
              position: 'relative',
              zIndex: 1
            }
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: 'rgba(17, 15, 30, 0.6)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 0,
              background: 'linear-gradient(125deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 40%, rgba(255, 255, 255, 0) 80%, rgba(255, 255, 255, 0.03) 100%)',
            },
            '& > *': {
              position: 'relative',
              zIndex: 1
            }
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            padding: '10px 20px',
            fontWeight: 600,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)'
            }
          },
          contained: {
            background: 'linear-gradient(45deg, rgba(92, 107, 192, 0.8), rgba(104, 118, 199, 0.9))',
            boxShadow: '0 4px 20px rgba(92, 107, 192, 0.4)',
            '&:hover': {
              background: 'linear-gradient(45deg, rgba(92, 107, 192, 0.9), rgba(126, 138, 210, 1))',
            }
          },
          containedSecondary: {
            background: 'linear-gradient(45deg, rgba(239, 83, 80, 0.8), rgba(244, 113, 110, 0.9))',
            boxShadow: '0 4px 20px rgba(239, 83, 80, 0.4)',
            '&:hover': {
              background: 'linear-gradient(45deg, rgba(239, 83, 80, 0.9), rgba(244, 143, 140, 1))',
            }
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px'
            }
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            padding: '0 4px',
            height: 'auto',
            minHeight: '32px',
            fontWeight: 500,
            '& .MuiChip-label': {
              padding: '6px 6px',
              whiteSpace: 'normal',
              lineHeight: 1.4
            },
            '& .MuiChip-icon': {
              marginLeft: 5,
              marginRight: -4
            },
            '&.MuiChip-sizeSmall': {
              height: 'auto',
              minHeight: '24px',
              '& .MuiChip-label': {
                padding: '2px 6px',
              },
              '& .MuiChip-icon': {
                fontSize: '0.75rem',
                marginLeft: 4
              }
            }
          },
          outlined: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          },
          filled: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }
        }
      }
    }
  });
};

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

// Apply dynamic background based on brand config
const AppContent: React.FC = () => {
  const brandConfig = getBrandConfig();
  
  // Set background gradient based on brand config
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
      body {
        background: linear-gradient(-45deg, ${brandConfig.background.join(', ')});
        background-size: 400% 400%;
        animation: gradient 15s ease infinite;
      }
    `;
    document.head.appendChild(styleEl);
    
    // Clean up
    return () => {
      document.head.removeChild(styleEl);
    };
  }, [brandConfig]);
  return (
    <Router>
      <Routes>
        {/* Special route for OBS overlay - no navbar, footer, or app container */}
        <Route path="/overlay/:gameId" element={
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent'
          }}>
            <LiveGameOverlay />
          </Box>
        } />
        
        {/* All other routes with normal layout */}
        <Route path="*" element={
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '100vh'
          }}>
            <Navbar />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/players" element={<Players />} />
                <Route path="/leagues" element={<Leagues />} />
                <Route path="/games" element={<Games />} />
                <Route path="/study" element={<Navigate to="/study/materials" />} />
                <Route path="/study/materials" element={<Study />} />
                <Route path="/study/simulator" element={<Study />} />
                <Route path="/player/:id" element={<PlayerProfile />} />
                <Route path="/league/:id" element={<LeagueStats />} />
                <Route path="/seating-plan/:id" element={<SeatingPlanView />} />
                <Route path="/quiz/:linkToken" element={<QuizPage />} />
                <Route path="/quiz/:linkToken/completed" element={<QuizCompleted />} />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/live-game-setup/:gameId" 
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/live-game-management/:gameId" 
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  } 
                />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        } />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  // Create theme using the dynamic branding
  const theme = createAppTheme();
  
  // Set document title based on brand config
  useEffect(() => {
    const brandConfig = getBrandConfig();
    document.title = brandConfig.name;
  }, []);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AdminRoleProvider>
          <AppContent />
        </AdminRoleProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;